/**
 * Pixel bridge: pick up custom conversions a pixel plugin sends straight to Meta.
 *
 * Why this exists. A WooCommerce shop running Pixel Manager announces its own
 * key events like this, and nowhere else:
 *
 *   fbq("trackCustom", "quote_requested", {value: 250, currency: "EUR"},
 *       {eventID: "pmw_ekuaujccfn2dl5fp"})
 *
 * Not on the dataLayer, not as a DOM event. Verified on a real store on
 * 5 August 2026. So a shop can have its conversions perfectly instrumented and
 * we would still see nothing, which reads to the customer as "your tracking
 * does not work".
 *
 * Three deliberate limits:
 *
 * 1. Only `trackCustom`. The standard funnel arrives through `track`, and we
 *    already measure that ourselves through the store detector and the
 *    dataLayer bridge. Listening to both would count every purchase twice,
 *    which is the exact bug we just spent a day removing.
 * 2. Only names the customer listed. Same gate as the dataLayer bridge: every
 *    event let through is an event we bill for.
 * 3. Nothing at all when that list is empty. We wrap a function that a
 *    customer's advertising depends on; without an opt-in there is no reason
 *    to touch it.
 *
 * The plugin's own `eventID` is carried over unchanged. That is the whole
 * trick: Meta sees our server-side event and their browser pixel event under
 * one id and merges them, instead of reporting two conversions.
 */

import { state, debugLog } from "./state.js";
import { sendEvent } from "./event-transport.js";
import { findMatchingRules } from "./url-matching.js";
import { listedNameFor } from "./data-layer-event-names.js";
import { buildCustomData } from "./custom-params.js";

/**
 * The pixel is loaded asynchronously, so it is usually not there yet when our
 * config arrives. Same shape as the consent watcher in consent.ts: check on an
 * interval, give up after a bounded time rather than polling forever.
 */
const WATCH_INTERVAL_MS = 500;
const WATCH_MAX_MS = 15_000;

/** The global shape lives in globals.d.ts; this only adds our own marker. */
type WrappedFbq = FbqFunction & FbqExtensions & { __tstWrapped?: boolean };

interface FbqOptions {
  eventID?: unknown;
}

function hasManualRule(eventName: string): boolean {
  const rules = findMatchingRules(window.location.href);
  return rules.some((rule) => rule.eventName === eventName);
}

/** Read one fbq call and forward it when it is a listed custom conversion. */
function observeCall(args: readonly unknown[]): void {
  // Our own pixel calls. Without this we would read back what we just sent.
  if (state.selfDispatch) return;

  const [method, rawName, rawParams, rawOptions] = args;
  if (method !== "trackCustom") return;
  if (typeof rawName !== "string") return;

  const eventName = listedNameFor(rawName, state.config?.dataLayerEvents);
  if (!eventName) return;

  if (hasManualRule(eventName)) {
    debugLog("fbqBridge", "skipped " + eventName + " — manual rule covers this URL");
    return;
  }

  const params = (rawParams && typeof rawParams === "object" ? rawParams : {}) as Record<string, unknown>;
  const options = (rawOptions && typeof rawOptions === "object" ? rawOptions : {}) as FbqOptions;

  const value = typeof params.value === "number" ? params.value : parseFloat(String(params.value));
  const eventId = typeof options.eventID === "string" && options.eventID.length > 0
    ? options.eventID
    : undefined;

  debugLog("fbqBridge", "bridging " + rawName + " -> " + eventName, "eventID=" + eventId);

  sendEvent({
    eventName,
    // Their id, not ours. This is what makes Meta merge the two reports of the
    // same conversion instead of adding them up.
    eventId,
    eventValue: isNaN(value) ? undefined : value,
    currency: params.currency != null ? String(params.currency) : undefined,
    customData: buildCustomData(params),
    source: "fbq",
    // The pixel already fired in the browser: that is the call we are reading.
    // Saying so keeps the dashboard honest about which channels covered this
    // event, and stops us from firing a second pixel for it.
    browserPixelFired: true,
  });
}

/**
 * Wrap window.fbq, passing every call through untouched.
 *
 * Returns false while the pixel does not exist yet, so the caller can try
 * again. Their pixel comes first at every step: observation happens in a
 * try/catch and the original is called no matter what.
 */
function wrapFbq(): boolean {
  const original = window.fbq as WrappedFbq | undefined;
  if (typeof original !== "function") return false;
  if (original.__tstWrapped) return true;

  const wrapped = function (this: unknown, ...args: unknown[]): void {
    try {
      observeCall(args);
    } catch (error) {
      // Never let our reading break someone's advertising.
      debugLog("fbqBridge", "observation failed: " + String(error));
    }
    original.apply(this, args);
  } as WrappedFbq;

  // Meta's snippet hangs state off the function itself (queue, loaded,
  // version, callMethod) and reads it back later. Dropping that would leave
  // the pixel with an empty queue.
  Object.keys(original).forEach((key) => {
    (wrapped as unknown as Record<string, unknown>)[key] = (original as unknown as Record<string, unknown>)[key];
  });
  // In that same snippet `n.push = n`, so push has to point at the wrapper too
  // or every queued call slips past us.
  if ((original.push as unknown) === original) wrapped.push = wrapped;

  wrapped.__tstWrapped = true;
  window.fbq = wrapped;
  if (window._fbq === original) window._fbq = wrapped;

  debugLog("fbqBridge", "installed");
  return true;
}

export function installFbqBridge(): void {
  const names = state.config?.dataLayerEvents;
  if (!names || names.length === 0) {
    debugLog("fbqBridge", "not installed — no custom event names configured");
    return;
  }

  if (wrapFbq()) return;

  const interval = setInterval(() => {
    if (wrapFbq()) clearInterval(interval);
  }, WATCH_INTERVAL_MS);
  setTimeout(() => clearInterval(interval), WATCH_MAX_MS);
}
